import React from 'react';
import {
  Box,
  Text,
  Link,
  Divider,
  useColorModeValue,
  Flex,
  Wrap,
  WrapItem,
  Button,
} from '@chakra-ui/react';
import { CustomImage } from '../atoms';
import { appInsights } from '../../appInsights';
// import { trackEvent } from '../../Analytics/ga';

interface FooterProps {
  footerBgColor: string;
  footerTextColor: string;
  fontSize?: string;
  footerNote?: string;
  links?: FooterLink[];
  translations?: TranslationLink[]; // New prop for translations
  imageSrc?: string;
  logoWidth?: { base: string; lg: string };
  logoPadding?: string;
  showFooterLogo: boolean;
  showFooterLinks: boolean;
  gameTitle?: string;
  showOnetrustCookieButton?: boolean; // New prop for OneTrust button
  useCustomOneTrustButtonStyle?: boolean; // New prop for custom button style
  oneTrustCookieButtonText?: string; // New prop for OneTrust button text
  openFooterLinkInNewTab: boolean;
}

export interface FooterLink {
  text: string;
  href: string;
  id: string | number;
  enabled: boolean;
}

export interface TranslationLink {
  text: string;
  id: string | number;
}

export const Footer: React.FC<FooterProps> = ({
  footerBgColor = 'gray.900',
  footerTextColor = 'gray.500',
  fontSize = 'sm',
  footerNote = '© 2024 Netflix',
  imageSrc,
  links = [],
  translations = [], // Default empty array
  logoWidth = { base: '50%', lg: '5%' },
  logoPadding = '4',
  showFooterLogo = true,
  showFooterLinks = true,
  gameTitle,
  showOnetrustCookieButton = false,
  useCustomOneTrustButtonStyle = false, // New prop for custom button style
  oneTrustCookieButtonText = undefined, // Default text for OneTrust button
  openFooterLinkInNewTab,
}) => {
  // Map links with translations based on id
  const translatedLinks = links.map(link => {
    const translation = translations.find(t => t.id === link.id);
    return {
      ...link,
      text: translation ? translation.text : link.text, // Use translated text if available
    };
  });

  return (
    <Box
      bg={footerBgColor}
      color={footerTextColor}
      textAlign="center"
      position="relative"
      overflow="hidden"
    >
      {/* Add a top margin to push content below the SVG */}
      <Box mt="2">
        {showFooterLogo && imageSrc && (
          <CustomImage src={imageSrc} width={logoWidth} margin="auto" padding={logoPadding} />
        )}
        <Text color={footerTextColor} fontSize={fontSize} mt="2">
          {gameTitle}
        </Text>
        <Text mt="1" color={footerTextColor} fontSize={fontSize}>
          {footerNote}
        </Text>
        <Divider my="4" borderColor={useColorModeValue('gray.600', 'gray.300')} />
        {showFooterLinks && translatedLinks.length > 0 && (
          <Flex direction="column" align="center" justify="center" px="2" py="2" pb="10">
            <Wrap spacing="24px" justify="center" align="center">
              {translatedLinks.map(link =>
                link.enabled ? (
                  <WrapItem key={link.id}>
                    <Link
                      href={link.href}
                      color={footerTextColor}
                      fontSize={{ base: 'sm', md: 'md' }}
                      _hover={{ color: useColorModeValue('gray.200', 'gray.600') }}
                      onClick={() => {
                        appInsights.trackEvent({
                          name: 'footer',
                          properties: {
                            buttonName: 'footerLinkButton',
                            linkId: link.id,
                            linkText: link.text,
                            linkHref: link.href,
                            userName: 'anonymous',
                          },
                        });
                        // trackEvent('footer_link_clicked', {
                        //   page: 'footer',
                        //   link_id: link.id,
                        //   link_text: link.text,
                        //   link_href: link.href,
                        // });
                      }}
                      target={openFooterLinkInNewTab ? '_blank' : undefined}
                      rel={openFooterLinkInNewTab ? 'noopener noreferrer' : undefined}
                    >
                      {link.text}
                    </Link>
                  </WrapItem>
                ) : null,
              )}

              {/* Cookie Settings Button */}
              {showOnetrustCookieButton && (
                <WrapItem>
                  <Button
                    id="ot-sdk-btn"
                    className="ot-sdk-show-settings"
                    size={useCustomOneTrustButtonStyle ? { base: 'sm' } : undefined}
                    bgGradient={
                      useCustomOneTrustButtonStyle ? 'linear(to-r, #4272D4, #8934C4)' : undefined
                    }
                    color={useCustomOneTrustButtonStyle ? 'white' : undefined}
                    _hover={
                      useCustomOneTrustButtonStyle
                        ? { bgGradient: 'linear(to-r, #5A8FF7, #A04EDB)' }
                        : undefined
                    }
                    onClick={() => {
                      // trackEvent('cookie_settings_clicked', {
                      //   page: 'footer',
                      //   button_name: 'cookieSettingsButton',
                      // });
                    }}
                  >
                    {useCustomOneTrustButtonStyle && oneTrustCookieButtonText
                      ? oneTrustCookieButtonText
                      : null}
                  </Button>
                </WrapItem>
              )}
            </Wrap>
          </Flex>
        )}
      </Box>
    </Box>
  );
};
